import { WidthProps } from 'types/styled'
import { formatDatetime } from 'utils/datetime'
import { formatCurrency } from 'utils/formatter'
import { FIELD_TYPE_ACCESSOR } from 'utils/constants'
import { EXAMPLE_URL_PLACEHOLDER } from 'utils/constants/url'
import { CustomFieldAccessor, CustomFieldPropertyValue, FixMeAny, OptionValue } from 'types/global'

import Box from '../Box'
import Text from '../Text'
import Field from '../Field'
import Dropdown from '../Dropdown'
import TextInput from '../TextInput'
import DatePicker, { PopupPosition } from '../DatePicker'
import Checkbox from '../Checkboxes/Checkbox'
import { DropdownItem } from '../Dropdown/types'
import LookupField from 'modules/Leads/shared/CustomLeadFormFields/FieldTypes/LookupField'

export interface Props {
  name?: string
  fieldTypeAccessor?: CustomFieldAccessor
  value?: FixMeAny
  onChange?: (e: FixMeAny) => void
  handleChange?: any
  label?: string
  readOnly?: boolean
  required?: boolean
  error?: string
  options?: OptionValue<string>[] | null
  property?: CustomFieldPropertyValue | null
  currencyISO?: string
  isEditable?: boolean
  isLoading?: boolean
  isDisabled?: boolean
  withNoneOption?: boolean
  placeholder?: string
  dataType?: 'string' | 'number'
  width?: WidthProps
  readOnlyFormat?: 'text' | 'field'
  popupPosition?: PopupPosition
  showMonthYearDropdown?: boolean
  maxLength?: number
  isCounterVisible?: boolean
}

export const DynamicInput = ({
  fieldTypeAccessor = 'text_input',
  label,
  name = '',
  value,
  onChange,
  readOnly = false,
  required = false,
  error,
  options = [],
  property,
  currencyISO,
  isEditable = true,
  isLoading,
  isDisabled,
  withNoneOption = false,
  placeholder,
  dataType,
  width,
  readOnlyFormat = 'field',
  popupPosition,
  showMonthYearDropdown,
  maxLength,
  isCounterVisible = false,
}: Props) => {
  const variant = label ? 'labeled' : undefined

  const commonProps = {
    name,
    label,
    value,
    onChange: (e: FixMeAny) => {
      if (dataType === 'number') {
        const num = Number(e.target.value)
        const isValidNum = !isNaN(num)

        onChange?.({
          target: {
            name,
            value: isValidNum ? num : e.target.value,
          },
        })
        return
      }

      onChange?.({
        target: {
          name,
          value: e.target.value,
        },
      })
    },
    required,
    error,
    isLoading,
    readOnly,
    isDisabled,
    disabled: isDisabled,
    placeholder,
    width,
  }

  const dropdownProps = {
    ...commonProps,
    options: options as DropdownItem<string>[],
    onChange: (e: DropdownItem<string> | Array<DropdownItem<string>>) => {
      if (Array.isArray(e)) {
        onChange?.({
          target: {
            name,
            value: e.map((i) => i.value),
          },
        })
        return
      }
      onChange?.({
        target: {
          name,
          value: e.value,
        },
      })
    },
    value: Array.isArray(value)
      ? options?.filter((i) => value.includes(i.value))
      : options?.find((i) => i.value === value),
  }

  if (!isEditable) {
    if (readOnlyFormat === 'text') {
      return (
        <Box>
          {label && <Text variant="h5">{label}</Text>}
          <Text>{value}</Text>
        </Box>
      )
    }

    switch (fieldTypeAccessor) {
      case FIELD_TYPE_ACCESSOR.singleSelect:
        return <Field label={label}>{options?.find((i) => i.value === value)?.label || '-'}</Field>
      case FIELD_TYPE_ACCESSOR.multiSelect:
        return (
          <Field label={label}>
            {options
              ?.filter((i) => i.value === value)
              ?.map((i) => i.value)
              .join(', ')}
          </Field>
        )
      case FIELD_TYPE_ACCESSOR.datePicker:
        return <Field label={label}>{formatDatetime(value, 'dd MMM yyyy') || '-'}</Field>
      case FIELD_TYPE_ACCESSOR.currency:
        if (!currencyISO || currencyISO === '-') {
          if (typeof value === 'number') {
            return <Field label={label}>{value.toLocaleString()}</Field>
          }

          return <Field label={label}>{value || '-'}</Field>
        }

        return <Field label={label}>{value ? `${formatCurrency(value, currencyISO)}` : '-'}</Field>
      case FIELD_TYPE_ACCESSOR.checkbox:
        return (
          <Checkbox checked={value} disabled>
            {label}
          </Checkbox>
        )
      case FIELD_TYPE_ACCESSOR.paragraph:
        return (
          <Field label={label} isParagraph>
            {value || '-'}
          </Field>
        )
      default:
        return <Field label={label}>{value || '-'}</Field>
    }
  }

  switch (fieldTypeAccessor) {
    case FIELD_TYPE_ACCESSOR.datePicker:
      return (
        <DatePicker
          {...commonProps}
          variant={variant}
          value={value}
          onChange={(dt: Date) =>
            onChange?.({
              target: {
                name,
                value: dt,
              },
            })
          }
          defaultToday={false}
          popupPosition={popupPosition}
          showMonthYearDropdown={showMonthYearDropdown}
        />
      )
    case FIELD_TYPE_ACCESSOR.paragraph:
      return <TextInput variant={variant} {...commonProps} type="textarea" />
    case FIELD_TYPE_ACCESSOR.multiSelect:
      return <Dropdown variant={variant} {...dropdownProps} withNoneOption={withNoneOption} isMulti />
    case FIELD_TYPE_ACCESSOR.singleSelect:
      return <Dropdown variant={variant} {...dropdownProps} withNoneOption={withNoneOption} />
    case FIELD_TYPE_ACCESSOR.currency:
      return (
        <TextInput
          variant={variant}
          {...commonProps}
          type="number"
          format="currency"
          addonBefore={currencyISO}
          placeholder="0.00"
        />
      )
    case FIELD_TYPE_ACCESSOR.number:
      return (
        <TextInput
          isCounterVisible={isCounterVisible}
          variant={variant}
          {...commonProps}
          type="number"
          placeholder={property?.is_decimal === 'decimal' ? '0.00' : '0'}
          format={property?.is_decimal === 'decimal' ? 'currency' : 'whole-positive'}
        />
      )
    case FIELD_TYPE_ACCESSOR.url:
      return <TextInput variant={variant} {...commonProps} type="url" placeholder={EXAMPLE_URL_PLACEHOLDER} />
    case FIELD_TYPE_ACCESSOR.checkbox:
      return (
        <Checkbox
          checked={value}
          disabled={isDisabled || readOnly || isLoading}
          isLoading={isLoading}
          name={name}
          onChange={(checked: boolean) => onChange?.({ target: { name, value: checked } })}
        >
          {label}
        </Checkbox>
      )
    case FIELD_TYPE_ACCESSOR.lookup:
      return (
        <LookupField
          {...{
            ...commonProps,
            onChange: (v) => {
              onChange?.({
                target: {
                  name,
                  value: v,
                },
              })
            },
          }}
        />
      )
    default:
      return <TextInput isCounterVisible={isCounterVisible} maxLength={maxLength} variant={variant} {...commonProps} />
  }
}

export default DynamicInput
